import { inject, injectable } from 'inversify'
import { chunk, uniq } from 'lodash-es'
import type { DependencyToPreload } from '@/services/types'
import type PreloaderManager from '@/services/preloaders/preloaderManager'
import { SERVICE_TYPES } from '@/core/container/types'
import type DomainManagerService from '@/services/domain/domainManagerService'
import { isServicePreloadableByIds } from '@/services/serviceGuards'
import type DeletedEntityService from '@/services/deletedEntityService'

@injectable()
export default class DependencyPreloaderService {
  constructor(
    @inject(SERVICE_TYPES.DomainManagerService) protected readonly domainManagerService: DomainManagerService,
    @inject(SERVICE_TYPES.PreloaderManager) protected readonly preloaderManager: PreloaderManager,
    @inject(SERVICE_TYPES.DeletedEntityService) private readonly deletedEntityService: DeletedEntityService,
  ) {}

  public async preload(dependencies: DependencyToPreload[], chunkSize: number = 20) {
    const dependenciesMap = new Map<DependencyToPreload['model'], string[]>()
    dependencies.forEach((dependency) => {
      const existingIds = dependenciesMap.get(dependency.model) || []
      dependenciesMap.set(dependency.model, [...existingIds, ...dependency.ids.map((id) => id.toString())])
    })

    const promises: Promise<unknown>[] = []
    dependenciesMap.forEach((ids, model) => {
      let domainService
      try {
        domainService = this.domainManagerService.getService(model)
      } catch {}

      const chunks = chunk(uniq(ids), chunkSize)
      chunks.forEach((chunkIds) => {
        if (domainService && isServicePreloadableByIds(domainService)) {
          promises.push(domainService.preloadByIds(chunkIds))
        } else {
          promises.push(this.preloaderManager.getByIdsPreloader(model).preloadByIds(model, chunkIds))
        }
      })
    })

    await Promise.all(promises)

    // Mark as deleted entities that were not preloaded
    dependenciesMap.forEach((ids, model) => {
      let domainService
      try {
        domainService = this.domainManagerService.getService(model)
      } catch {}
      if (!domainService) {
        return
      }
      const deletedEntitiesIds = ids.filter((id) => {
        return domainService.findEntityByIdOrNull(id) === null
      })
      this.deletedEntityService.markManyAsDeleted(model.entity, deletedEntitiesIds)
    })
  }
}
